<template>
  <TransitionRoot
    :show="modelValue"
    as="template"
    enter="duration-300 ease-out"
    enter-from="opacity-0"
    enter-to="opacity-100"
  >
    <Dialog
      :open="modelValue"
      as="div"
      class="relative z-50 text-gray-700"
      @close="emit('update:modelValue', false)"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
      >
        <div
          class="fixed inset-0 bg-black/30"
          aria-hidden="true"
        />
      </TransitionChild>
      <div class="fixed inset-0 flex items-center justify-center p-2 md:px-0">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
        >
          <DialogPanel
            class="relative flex h-[420px] max-h-[40rem] w-full max-w-lg flex-col overflow-y-scroll rounded-md bg-white p-3 md:h-[500px] md:p-5"
          >
            <p
              class="absolute left-0 top-0 w-full bg-lk-light-purple px-2 py-1 text-center text-xs text-white md:text-sm"
            >
              ¡DESCUBRE LA NUEVA FORMA DE SUBIR FACTURAS!
            </p>
            <button
              class="ml-auto focus:outline-none"
              @click="emit('update:modelValue', false)"
            >
              <XMarkIcon class="mt-3 size-5 fill-gray-700 md:size-6" />
            </button>
            <h2 class="font-serif text-xl md:text-2xl">
              {{ props.subOrder.purchaseInvoiceConciliationNumbers?.length ? 'Editar' : 'Subir' }}
              factura del pedido #{{ props.subOrder.id }}
            </h2>
            <div class="flex size-full flex-col">
              <div
                class="mt-2 flex w-full flex-col text-sm md:mt-3 md:text-base"
                :class="{ 'hover:bg-gray-50': !showTransferPaymentInformation }"
              >
                <button
                  class="flex w-full items-center"
                  type="button"
                  @click="(showTransferPaymentInformation = !showTransferPaymentInformation)"
                >
                  <span>Mostrar datos de faturación</span>
                  <ChevronDownIcon
                    class="ml-2 size-3 md:size-4"
                    :class="{
                      'rotate-180 duration-150': showTransferPaymentInformation,
                      'duration-150': !showTransferPaymentInformation,
                    }"
                  />
                </button>
                <lokal-transfer-payment-information
                  v-if="showTransferPaymentInformation"
                  class="ml-2 text-xs md:text-sm"
                />
              </div>
              <button
                class="mt-1 flex items-center gap-2 text-sm hover:bg-gray-50 md:mt-1.5 md:text-base"
                type="button"
                @click="(showTotalsDetails = !showTotalsDetails)"
              >
                <span>Monto neto a facturar: {{ useFormatCurrency(amountToBill) }}</span>
                <ChevronDownIcon
                  class="size-4 fill-gray-700 transition-transform duration-150"
                  :class="{ 'rotate-180': showTotalsDetails }"
                />
              </button>
              <p
                v-if="showTotalsDetails"
                class="ml-2 text-xs font-light md:text-sm"
              >
                Neto total: {{ useFormatCurrency(subOrder.totalWholesaleAmountCents) }}
                <br />
                Descuento total productos: {{ useFormatCurrency(subOrder.totalMakerDiscountAmountCents) }}
                <br />
                Descuento por volumen: {{ useFormatCurrency(subOrder.volumeDiscountAmountCents) }}
                <br />
                Descuento por código: {{ useFormatCurrency(subOrder.makerDiscountCodeAmountCents) }}
                <br />
                <span v-if="props.maker.billingMethod !== 'full_invoice'">
                  Comisión Lokal: {{ useFormatCurrency(totalCommission) }}
                </span>
                <br />
                <span v-if="props.subOrder.advancePaymentFeeValue">
                  Costo de pago adelantado: {{ useFormatCurrency(subOrder.advancePaymentFeeAmountCents) }}
                </span>
              </p>
              <div class="mt-1 w-full md:mt-1.5">
                <downloadable-files-form
                  :action="`/sub_orders/${props.subOrder.id}/download_excel`"
                  label="Descargar detalle de facturación"
                  classes="text-left text-sm md:text-base hover:underline"
                />
              </div>
              <purchase-invoice-conciliations-form
                :sub-order="props.subOrder"
                :maker="props.maker"
                @reload:sub-order="emit('reload:subOrder')"
                @close-dialog="emit('update:modelValue', false)"
              />
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script setup lang="ts">
import { XMarkIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { ref, computed } from 'vue';
import { Dialog, DialogPanel, TransitionRoot, TransitionChild } from '@headlessui/vue';
import useFormatCurrency from './use/format-currency';
import useBillingTools from './use/billing-tools';
import lokalTransferPaymentInformation from './shared/lokal-transfer-payment-information.vue';
import purchaseInvoiceConciliationsForm from './purchase-invoice-conciliations-form.vue';
import downloadableFilesForm from './shared/downloadable-files-form.vue';

interface SubOrderBillListDialogProps {
  modelValue: boolean;
  subOrderId: number;
  subOrder: SubOrder;
  maker: Maker;
}

const props = withDefaults(defineProps<SubOrderBillListDialogProps>(), {
  modelValue: false,
});

const emit = defineEmits<{ (e: 'update:modelValue', value: boolean): void; (e: 'reload:subOrder'): void }>();

const showTotalsDetails = ref(false);
const showTransferPaymentInformation = ref(false);

const { billTools } = useBillingTools();

const commissionFraction = computed(() => props.subOrder.commission / billTools.value.totalPorcentage);
const makerDiscountedAmount = computed(
  () => props.subOrder.totalWholesaleAmountCents - props.subOrder.totalMakerDiscountAmountCents,
);
const makerDiscountedAmountWithoutVolume = computed(
  () =>
    makerDiscountedAmount.value -
    props.subOrder.volumeDiscountAmountCents -
    props.subOrder.makerDiscountCodeAmountCents,
);
const totalCommission = computed(() => makerDiscountedAmountWithoutVolume.value * commissionFraction.value);
const amountToBill = computed(() => {
  let base = makerDiscountedAmountWithoutVolume.value;
  if (props.maker.billingMethod === 'full_invoice') return base;
  if (props.subOrder.advancePaymentFeeValue) {
    base -= props.subOrder.advancePaymentFeeAmountCents;
  }

  return base - totalCommission.value;
});
</script>
